import React from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  useRecipes,
  useRecipeOperations,
  useRecipeSearch,
} from "../../../hooks/useRecipes";
import { useSearchableList } from "../../../hooks/useSearchableList";
import { useCrudModals } from "../../../hooks/useCrudModals";
import { Recipe } from "../../../types/recipe";
import { RecipeService } from "../../../services/recipeService";
import { toRecipeInput } from "../../../utils/recipeUtils";
import RecipesTable from "../../../components/recipes/RecipesTable";
import RecipeModal from "../../../components/recipes/RecipeModal";
import DeleteConfirmationModal from "../../../components/modals/DeleteConfirmationModal";
import Pagination from "../../../components/common/Pagination";
import ListPageShell from "../../../components/common/ListPageShell";

const LIMIT = 15;

const Nutrition: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const modals = useCrudModals<Recipe>();

  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialSearchTerm = searchParams.get("search") || "";

  const { recipes, ...recipeList } = useRecipes(initialPage, LIMIT);
  const recipeSearch = useRecipeSearch();

  const list = useSearchableList<Recipe>({
    list: { items: recipes, ...recipeList },
    search: recipeSearch,
    initialSearchTerm,
    initialPage,
  });

  const {
    loading: operationLoading,
    createAdminRecipe,
    updateRecipe,
    deleteRecipe,
    error: operationError,
  } = useRecipeOperations();

  // The list row is a summary; the edit form needs the full recipe.
  const handleEditRecipe = async (recipe: Recipe) => {
    const full = await RecipeService.getRecipeById(recipe.id);
    modals.openEdit(full ?? recipe);
  };

  const handleViewRecipe = (recipe: Recipe) => {
    navigate(`/admin/nutrition/${recipe.id}`, {
      state: { from: location.search },
    });
  };

  const confirmDeleteRecipe = async () => {
    if (!modals.toDelete) return;

    const success = await deleteRecipe(modals.toDelete.id);
    if (success) {
      list.refreshAll();
      modals.closeDelete();
    }
  };

  const handleCreateSubmit = async (recipeData: any) => {
    const newRecipe = await createAdminRecipe(toRecipeInput(recipeData));
    if (newRecipe) {
      list.refreshAll();
      modals.closeCreate();
    }
  };

  const handleUpdateSubmit = async (recipeData: any) => {
    if (!modals.selected) return;

    const updatedRecipe = await updateRecipe(
      modals.selected.id,
      toRecipeInput(recipeData)
    );
    if (updatedRecipe) {
      list.refreshAll();
      modals.closeEdit();
    }
  };

  return (
    <>
      <ListPageShell
        entityName="recipes"
        onRefresh={list.handleRefresh}
        refreshDisabled={list.listLoading}
        onAdd={modals.openCreate}
        addLabel="Add Recipe"
        error={list.error}
        errorContext={list.hasSearched ? "searching" : "loading"}
        operationError={operationError}
      >
        <RecipesTable
          recipes={list.items}
          loading={list.loading || operationLoading}
          onEdit={handleEditRecipe}
          onDelete={modals.openDelete}
          onView={handleViewRecipe}
          onSearch={list.handleSearch}
          onClearSearch={list.handleClearSearch}
          searchTerm={list.lastSearchTerm}
          isSearchMode={list.hasSearched}
        />

        <Pagination
          page={list.page}
          limit={LIMIT}
          total={list.total}
          totalPages={list.totalPages}
          entityName="recipes"
          isSearchMode={list.hasSearched}
          onPageChange={list.onPageChange}
          loading={list.loading}
        />
      </ListPageShell>

      <RecipeModal
        isOpen={modals.showCreate}
        onClose={modals.closeCreate}
        onSubmit={handleCreateSubmit}
        mode="create"
        loading={operationLoading}
        error={operationError}
      />

      <RecipeModal
        isOpen={modals.showEdit}
        onClose={modals.closeEdit}
        onSubmit={handleUpdateSubmit}
        recipe={modals.selected}
        mode="edit"
        loading={operationLoading}
        error={operationError}
      />

      <DeleteConfirmationModal
        isOpen={modals.showDelete}
        onClose={modals.closeDelete}
        onConfirm={confirmDeleteRecipe}
        title="Delete Recipe"
        message="Are you sure you want to delete this recipe?"
        itemName={modals.toDelete?.name || "Untitled Recipe"}
        loading={operationLoading}
        error={operationError}
      />
    </>
  );
};

export default Nutrition;
